import { GpsDevice } from '@/types/gps';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Edit, Trash2, MapPin, Signal, Battery } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface DeviceDetailsSheetProps {
  device: GpsDevice | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit: (device: GpsDevice) => void;
  onDelete: (deviceId: string) => void;
}

export function DeviceDetailsSheet({
  device,
  open,
  onOpenChange,
  onEdit,
  onDelete,
}: DeviceDetailsSheetProps) {
  if (!device) return null;

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive'> = {
      moving: 'default',
      idle: 'secondary',
      offline: 'destructive',
      sleep: 'secondary',
      unknown: 'secondary',
    };
    
    const labels: Record<string, string> = {
      moving: 'Em Movimento',
      idle: 'Parado',
      offline: 'Offline',
      sleep: 'Suspenso',
      unknown: 'Desconhecido',
    };

    return <Badge variant={variants[status] || 'secondary'}>{labels[status] || status}</Badge>;
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:w-[650px] p-6 overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Detalhes do Equipamento</SheetTitle>
          <SheetDescription>
            {device.model || device.code}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6 mt-6">
          {/* Informações Básicas */}
          <div className="space-y-3">
            <h3 className="font-semibold text-sm text-muted-foreground uppercase">
              Identificação
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-xs text-muted-foreground">Código:</span>
                <div className="font-mono text-sm mt-1">{device.code || '-'}</div>
              </div>
              <div>
                <span className="text-xs text-muted-foreground">IMEI:</span>
                <div className="font-mono text-sm mt-1">{device.imei_or_uid || '-'}</div>
              </div>
              <div>
                <span className="text-xs text-muted-foreground">Modelo:</span>
                <div className="text-sm mt-1">{device.model || '-'}</div>
              </div>
              <div>
                <span className="text-xs text-muted-foreground">Protocolo:</span>
                <div className="text-sm mt-1">{device.protocol || '-'}</div>
              </div>
            </div>
          </div>

          {/* Chip/SIM */}
          {device.sim_iccid && (
            <div className="space-y-3">
              <h3 className="font-semibold text-sm text-muted-foreground uppercase">
                Chip M2M
              </h3>
              <div className="space-y-2">
                {device.chip_operator && (
                  <div>
                    <span className="text-xs text-muted-foreground">Operadora:</span>
                    <div className="mt-1">
                      <Badge variant="outline" className="text-xs">
                        {device.chip_operator === 'tim' && (
                          <span className="flex items-center gap-1">
                            <div className="w-2 h-2 rounded-full bg-blue-600" />
                            TIM
                          </span>
                        )}
                        {device.chip_operator === 'claro' && (
                          <span className="flex items-center gap-1">
                            <div className="w-2 h-2 rounded-full bg-red-600" />
                            Claro
                          </span>
                        )}
                        {device.chip_operator === 'vivo' && (
                          <span className="flex items-center gap-1">
                            <div className="w-2 h-2 rounded-full bg-purple-600" />
                            Vivo
                          </span>
                        )}
                        {device.chip_operator === 'm2m' && (
                          <span className="flex items-center gap-1">
                            <div className="w-2 h-2 rounded-full bg-gray-600" />
                            M2M
                          </span>
                        )}
                      </Badge>
                    </div>
                  </div>
                )}
                <div>
                  <span className="text-xs text-muted-foreground">ICCID:</span>
                  <div className="font-mono text-xs mt-1 break-all">{device.sim_iccid}</div>
                </div>
              </div>
            </div>
          )}

          {/* Veículo Vinculado */}
          <div className="space-y-3">
            <h3 className="font-semibold text-sm text-muted-foreground uppercase">
              Veículo Vinculado
            </h3>
            {device.vehicle_id ? (
              <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                <MapPin className="h-5 w-5 text-primary" />
                <div>
                  <div className="font-semibold">{device.vehicle_plate}</div>
                  <div className="text-xs text-muted-foreground">
                    {device.vehicle_make} {device.vehicle_model}
                  </div>
                </div>
              </div>
            ) : (
              <Badge variant="secondary" className="w-fit">
                Sem veículo vinculado
              </Badge>
            )}
          </div>

          {/* Status e Conexão */}
          <div className="space-y-3">
            <h3 className="font-semibold text-sm text-muted-foreground uppercase">
              Status e Conexão
            </h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Signal className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Status:</span>
                </div>
                {getStatusBadge(device.status)}
              </div>
              
              {device.battery_level !== undefined && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Battery className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Bateria:</span>
                  </div>
                  <span className="text-sm font-medium">{device.battery_level}%</span>
                </div>
              )}

              {device.last_signal_at && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Último sinal:</span>
                  <span className="text-sm">
                    {formatDistanceToNow(new Date(device.last_signal_at), {
                      addSuffix: true,
                      locale: ptBR,
                    })}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Ações */}
          <div className="flex gap-2 pt-4 border-t">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => {
                onEdit(device);
                onOpenChange(false);
              }}
            >
              <Edit className="h-4 w-4 mr-2" />
              Editar
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                onDelete(device.id);
                onOpenChange(false);
              }}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Excluir
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
